/**
 * Shared setting-renderer styles.
 *
 * The setting renderers in this folder share the same header scaffolding: a
 * padded vertical container, a header row that spreads its label block and
 * trailing control apart, the stacked label/description block, and the
 * label/description typography. These `GriffelStyle` fragments are referenced
 * inside each renderer's own `makeStyles`, so the generated atomic classes stay
 * identical while the definitions live in one place.
 *
 * Usage:
 * ```ts
 * const useStyles = makeStyles({
 *   container: settingContainerStyle,
 *   header: settingHeaderStyle,
 *   labelContainer: settingLabelContainerStyle,
 *   label: settingLabelStyle,
 *   description: settingDescriptionStyle,
 *   // ...renderer-specific rules
 * });
 * ```
 */

import { tokens, type GriffelStyle } from '@coreai/fluentui-react';

/** Padded vertical flex container wrapping a setting renderer. */
export const settingContainerStyle: GriffelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacingVerticalS,
  padding: tokens.spacingVerticalM,
  paddingLeft: tokens.spacingHorizontalM,
  paddingRight: tokens.spacingHorizontalM,
};

/** Header row: label block on the left, trailing control on the right. */
export const settingHeaderStyle: GriffelStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

/** Stacked label + description block. */
export const settingLabelContainerStyle: GriffelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacingVerticalXXS,
};

/** Setting label typography. */
export const settingLabelStyle: GriffelStyle = {
  fontSize: tokens.fontSizeBase300,
  fontWeight: tokens.fontWeightRegular,
  color: tokens.colorNeutralForeground1,
};

/** Setting description/help typography. */
export const settingDescriptionStyle: GriffelStyle = {
  fontSize: tokens.fontSizeBase200,
  color: tokens.colorNeutralForeground3,
};
